// How the header splits the org's game catalogue: what the game switcher lists, and what the Games page offers to
// request.
//
// The switcher lists the games the user can open - the same set as the sidebar - and links to the Games page, which
// is where a member finds the org's other games and asks for access (decided 11 October 2026). It used to list the
// locked games too, greyed with "Request access", which buried discovery in a dropdown.

/**
 * Lifecycle states a game the user cannot open is NOT offered in: one still being set up (draft), or one closed
 * (suspended, sunset, archived), cannot usefully be requested - and listing someone else's draft is what 2832416c6
 * was avoiding.
 */
export const NOT_REQUESTABLE_STATES = new Set(['DRAFT', 'AUTOSAVED', 'SUSPENDED', 'SUNSET', 'ARCHIVED']);

const stateOf = (game) => String(game?.state?.code || game?.state || '').toUpperCase();

const byName = (a, b) => String(a.gameName || a.id).localeCompare(String(b.gameName || b.id));

/** The org catalogue as the switcher lists it: the games you can open, whatever their state, by name. */
export const switcherGames = (catalogue = []) => (Array.isArray(catalogue) ? catalogue : [])
    .filter((g) => g && g.id && g.accessible)
    .sort(byName);

/**
 * The games the Games page offers to request: ones the user cannot open and was never given - a game they are
 * suspended on is theirs, shown muted with their own games (state.suspendedGames) - in a state worth asking for.
 */
export const requestableGames = (catalogue = []) => (Array.isArray(catalogue) ? catalogue : [])
    .filter((g) => g && g.id && !g.accessible && !g.suspended && !NOT_REQUESTABLE_STATES.has(stateOf(g)))
    .sort(byName);

/** The list header's count: matches of all while searching, otherwise the total. */
export const switcherCountLabel = ({ items = [], matched = null }) => (
    matched !== null ? `${matched} of ${items.length}` : `${items.length} total`
);

/**
 * The lifecycle state a switcher row shows and blocks on: SUSPENDED or SUNSET, else null. The switcher had the pills
 * and the blocking for these but was never given them for a game, so a suspended or sunset game read as ordinary and
 * was selectable - the lock card only appeared after landing on it.
 */
export const lifecycleStateOf = (game) => {
    const state = stateOf(game);
    return state === 'SUSPENDED' || state === 'SUNSET' ? state : null;
};

/**
 * The org switcher's pill for an org that is not running: 'Suspended' or 'Archived', else null. Unlike a frozen game
 * the org stays selectable - its owners reach its management pages through it - but every one of its games is
 * closed, and the pill says why before the user lands on a lock card.
 */
export const orgStatusLabel = (orgState) => {
    const state = String(orgState || '').toUpperCase();
    if (state === 'SUSPENDED') return 'Suspended';
    if (state === 'ARCHIVED') return 'Archived';
    return null;
};
